import { useEffect, useState } from "react";
import cn from "classnames";
import { FileWithPath, useDropzone } from "react-dropzone";

// components
import { Button, Input } from "components/LibraryComponents/Components";
import { CheckIcon, Spinner, UploadCloud, XIcon } from "components/icons";
import ReplaceCapabilitiesStatmentDialog from "./ReplaceCapabilitiesStatmentDialog";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";

// styles
import styles from "./OverviewSection.module.scss";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// api
import {
  uploadCapabilityStatmentFile,
  updateExtendedProfileData,
} from "data/fetch";

type CapabilitiesStatementProps = {
  uei: string;
  statementLink: string;
  isSaveBtnDisabled: boolean;
  isSaveBtnLoading: boolean;
  handleChange: (value: string) => void;
  handleSave: () => void;
  lastSavedSection?: string;
  fieldToConfirmationMap: Record<string, string>;
  clearConfirmation: () => void;
};

export default function CapabilitiesStatement({
  uei,
  statementLink,
  isSaveBtnDisabled,
  isSaveBtnLoading,
  handleChange,
  handleSave,
  lastSavedSection,
  fieldToConfirmationMap,
  clearConfirmation
}: CapabilitiesStatementProps) {
  // edit profile data store
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [validation, setValidation] = useState<{
    title: string;
    desc: string;
    isError: boolean;
  } | null>(null);
  const [tempFile, setTempFile] = useState<FileWithPath | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Helper function to format file size error messages
  const formatFileSizeError = (
    errorMessage: string,
    file?: FileWithPath
  ): string => {
    if (errorMessage.includes("bytes") && file) {
      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
      return `File size (${fileSizeInMB} MB) exceeds the maximum limit of 10 MB`;
    }
    return errorMessage;
  };

  const { open, getRootProps, getInputProps } = useDropzone({
    accept: {
      "application/pdf": [".pdf"], // Accept only PDF files
    },
    maxFiles: 1, // Limit to 1 file
    maxSize: 10 * 1024 * 1024, // 10MB
    noClick: true, // Do not allow click to open file dialog
    noKeyboard: true, // Do not allow keyboard to open file dialog
    onDrop: async (acceptedFiles: FileWithPath[]) => {
      try {
        // Handle the accepted files here
        if (acceptedFiles.length > 0) {
          // check if url already exists
          if (statementLink) {
            setIsOpen(true);
            setTempFile(acceptedFiles[0]);
            return;
          }

          handleFileUpload(acceptedFiles[0]);
        }
      } catch (error) {
        setValidation({
          title: "Upload failed",
          desc: "There was an error uploading your PDF.",
          isError: true,
        });

        console.error(error.message);
      }
    },
    onDropRejected: (rejectedFiles) => {
      // Handle rejected files here
      console.log("Rejected files:", rejectedFiles);

      if (rejectedFiles.length > 0) {
        const rejectedFile = rejectedFiles[0];
        const errorMessage = formatFileSizeError(
          rejectedFile.errors[0].message,
          rejectedFile.file
        );

        setValidation({
          title: "Upload failed",
          desc: errorMessage,
          isError: true,
        });
      } else {
        setValidation({
          title: "Upload failed",
          desc: "There was an error uploading your PDF. Please try again in a few minutes.",
          isError: true,
        });
      }
    },
  });

  const handleStatementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    clearConfirmation();

    // URL validation
    // const urlPattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w- ./?%&=]*)?$/i;

    handleChange(value);

    if (value === "") {
      setValidation(null);
      return;
    }

    try {
      new URL(value)
      setValidation(null);
    } catch {
      setValidation({
        title: "Invalid url",
        desc: "Please enter a valid URL.",
        isError: true,
      });
    }
  };

  const handleFileUpload = async (file: FileWithPath) => {
    try {
      setIsLoading(true);
      setValidation(null);

      // Use FormData approach - pass the file directly to the upload function
      const response = await uploadCapabilityStatmentFile(extendedProfile.uei, file);
      const data = await response.json();

      // The backend should return the uploaded file URL in the response
      const uploadedUrl = data.capabilities_statement_link;

      handleChange(uploadedUrl);

      setExtendedProfile({
        ...extendedProfile,
        capabilities_statement_link: uploadedUrl,
      });

      setIsLoading(false);

      setValidation({
        title: "Upload successful",
        desc: "Your profile has successfully been updated.",
        isError: false,
      });
    } catch (error) {
      setIsLoading(false);

      setValidation({
        title: "Upload failed",
        desc: "There was an error uploading your PDF. Please try again in a few minutes.",
        isError: true,
      });

      console.error(error.message);
    }
  };

  const handleClick = () => {
    if (tempFile) {
      handleFileUpload(tempFile);
      setTempFile(null);
    }

    setIsOpen(false);
  };

  return (
    <>
      <div className={cn(styles["section-content"], styles["space-y-6"])}>
        <div>
          <h3 className={styles["title"]}>Capabilities Statement</h3>
          <p className={styles["desc"]}>
            Upload a PDF of your capabilities statement, or enter a url to a
            publicly available PDF file.
          </p>
        </div>

        <Input
          id="statement"
          name="statement"
          type="text"
          label="Capabilities Statement Link"
          placeholder="Upload a file below, or enter the url to a file here"
          value={statementLink}
          onChange={handleStatementChange}
        />

        {validation && (
          <div className={styles["validation-section"]}>
            {validation.isError ? (
              <XIcon className={cn(styles["icon-wrapper"], styles["error"])} />
            ) : (
              <CheckIcon
                className={cn(styles["icon-wrapper"], styles["success"])}
              />
            )}

            <div
              className={cn(styles["validation-content"], styles["success"])}
            >
              <p>{validation.title}</p>
              <p>{validation.desc}</p>
            </div>
          </div>
        )}

        <div {...getRootProps({ className: styles["drop-zone-section"] })}>
          <div
            className={cn(styles["drop-zone-content"], {
              [styles["hide"]]: isLoading,
            })}
          >
            <input {...getInputProps()} />

            {!validation && statementLink && (
              <div className={styles["success-section"]}>
                <CheckIcon className={styles["check-icon"]} />
                <p className={styles["upload-link"]}>{statementLink}</p>
              </div>
            )}

            <UploadCloud className={styles["upload-icon"]} />

            <h5>Drag and drop to upload a file, or</h5>

            <Button
              type="button"
              className={styles["browser-btn"]}
              onClick={open}
            >
              Browse files
            </Button>

            <div className={styles["info-section"]}>
              <p>Maximum file size: 10MB</p>
              <p>Supported file types: PDF</p>
            </div>
          </div>

          {isLoading && (
            <div className={styles["file-upload-spinner"]}>
              <Spinner />
            </div>
          )}
        </div>

        <ResponsiveStack>
          <Button
            type="button"
            loading={isSaveBtnLoading}
            disabled={isSaveBtnDisabled}
            onClick={handleSave}
          >
            {isSaveBtnLoading ? "Saving..." : "Save changes"}
          </Button>
          {lastSavedSection === fieldToConfirmationMap.capabilities_statement_link && <Saved />}
        </ResponsiveStack>
      </div>

      {isOpen && (
        <ReplaceCapabilitiesStatmentDialog
          open={isOpen}
          setOpen={setIsOpen}
          handleClick={handleClick}
        />
      )}
    </>
  );
}
